<template>
  <div class="admin-wrapper">
    <!-- Background orbs -->
    <div class="bg-orb bg-orb-1"></div>
    <div class="bg-orb bg-orb-2"></div>

    <!-- ── HEADER ────────────────────────────────── -->
    <header class="admin-header glass">
      <div class="header-brand">
        <img src="@/assets/logo-white.png" alt="Logo" class="header-logo" />
        <div>
          <span class="header-title">Fino Trato</span>
          <span class="header-sub">Painel Administrativo</span>
        </div>
      </div>

      <div class="header-stats">
        <div class="stat-pill glass">
          <span class="stat-num">{{ pets.length }}</span>
          <span class="stat-label">Total</span>
        </div>
        <div class="stat-pill glass missing-pill" v-if="missingCount > 0">
          <span class="stat-num">{{ missingCount }}</span>
          <span class="stat-label">Desaparecidos</span>
        </div>
      </div>

      <div class="header-tabs">
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'pets' }"
          @click="activeTab = 'pets'"
        >
          <v-icon dark size="15">mdi-paw</v-icon>
          Pets
        </button>
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'logs' }"
          @click="switchToLogs"
        >
          <v-icon dark size="15">mdi-map-search-outline</v-icon>
          Rastreamento
          <span v-if="logs.length" class="tab-badge">{{ logs.length }}</span>
        </button>
      </div>

      <div class="header-actions">
        <button class="glass-btn" @click="openAddModal" v-if="activeTab === 'pets'">
          <v-icon dark size="16">mdi-plus</v-icon>
          Novo Pet
        </button>
        <span class="admin-nome-label" v-if="adminNome">
          <v-icon dark size="14">mdi-account-circle-outline</v-icon>
          {{ adminNome }}
        </span>
        <router-link to="/" class="glass-btn" title="Início">
          <v-icon dark size="16">mdi-home-outline</v-icon>
        </router-link>
        <button class="glass-btn btn-logout" @click="logout" title="Sair">
          <v-icon dark size="16">mdi-logout</v-icon>
        </button>
      </div>
    </header>

    <!-- ── BODY ──────────────────────────────────── -->
    <div class="admin-body">

      <!-- SIDEBAR -->
      <aside class="admin-sidebar glass">
        <div class="sidebar-search">
          <v-icon dark class="sb-search-icon" size="18">mdi-magnify</v-icon>
          <input
            v-model="search"
            class="glass-input sb-input"
            placeholder="Buscar pet ou tutor..."
          />
        </div>

        <div class="pet-list custom-scroll">
          <div
            v-for="pet in filteredPets"
            :key="pet.Id"
            class="pet-item"
            :class="{
              active: selectedPet && selectedPet.Id === pet.Id,
              missing: pet.isDesaparecido,
            }"
            @click="selectedPet = pet"
          >
            <div class="pet-item-avatar">
              <img
                :src="pet.B64Foto || require('@/assets/logoCard.png')"
                class="pet-thumb"
                @error="onImgError"
              />
              <span v-if="pet.isDesaparecido" class="missing-dot"></span>
            </div>
            <div class="pet-item-info">
              <span class="pit-name">{{ pet.Nome || 'Cadastro Pendente' }}</span>
              <span class="pit-meta">{{ pet.Raca || pet.TipoPet || pet.IdMicrochip || '—' }} · {{ pet.Portador || '—' }}</span>
            </div>
          </div>

          <div v-if="filteredPets.length === 0 && !loading" class="sb-empty">
            <v-icon dark color="rgba(255,255,255,0.3)" size="32">mdi-paw-off</v-icon>
            <p>Nenhum pet encontrado</p>
          </div>

          <div v-if="loading" class="sb-empty">
            <v-icon dark color="rgba(255,255,255,0.4)" size="28" class="spin">mdi-loading</v-icon>
            <p>Carregando...</p>
          </div>
        </div>
      </aside>

      <!-- CONTENT -->
      <main class="admin-content custom-scroll">

        <!-- ── ABA LOGS ────────────────────────────── -->
        <div v-if="activeTab === 'logs'" class="logs-section">
          <div class="logs-header-row">
            <h3 class="logs-title">
              <v-icon dark size="18" class="mr-1">mdi-map-search-outline</v-icon>
              Rastreamento de Pets Desaparecidos
            </h3>
            <button class="glass-btn logs-refresh" @click="loadLogs" :disabled="loadingLogs">
              <v-icon dark size="15" :class="{ spin: loadingLogs }">mdi-refresh</v-icon>
              Atualizar
            </button>
          </div>

          <p class="logs-desc">
            Registros de visitas à página pública de pets marcados como desaparecidos. A localização é coletada com consentimento do navegador.
          </p>

          <div v-if="loadingLogs" class="logs-empty">
            <v-icon dark size="36" color="rgba(255,255,255,0.3)" class="spin">mdi-loading</v-icon>
            <p>Carregando registros...</p>
          </div>

          <div v-else-if="logs.length === 0" class="logs-empty">
            <v-icon dark size="36" color="rgba(255,255,255,0.25)">mdi-map-marker-off-outline</v-icon>
            <p>Nenhum registro ainda.<br/>Quando a página de um pet desaparecido for acessada, o registro aparecerá aqui.</p>
          </div>

          <div v-else class="logs-table-wrap glass">
            <table class="logs-table">
              <thead>
                <tr>
                  <th>Pet</th>
                  <th>Data / Hora</th>
                  <th>Localização</th>
                  <th>IP</th>
                  <th>Dispositivo</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="log in logs" :key="log.Id" class="log-row">
                  <td class="log-pet-name">
                    <v-icon dark size="13" class="mr-1" color="#e74c3c">mdi-paw</v-icon>
                    {{ log.PetNome }}
                  </td>
                  <td class="log-time">{{ formatDateTime(log.Timestamp) }}</td>
                  <td class="log-location">
                    <a
                      v-if="log.Latitude && log.Longitude"
                      :href="`https://www.google.com/maps?q=${log.Latitude},${log.Longitude}`"
                      target="_blank"
                      class="maps-link"
                    >
                      <v-icon dark size="13">mdi-map-marker</v-icon>
                      {{ log.Latitude }}, {{ log.Longitude }}
                    </a>
                    <span v-else class="log-muted">Não disponível</span>
                  </td>
                  <td class="log-ip">{{ log.IpAddress || '—' }}</td>
                  <td class="log-ua">{{ truncateUA(log.UserAgent) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ── ABA PETS ────────────────────────────── -->
        <transition v-else name="detail-fade" mode="out-in">
        <div v-if="!selectedPet" key="dashboard" class="dashboard">

          <!-- Saudação -->
          <div class="dash-greeting">
            <div>
              <h2 class="dash-title">{{ saudacao }}, {{ adminNome || 'Admin' }}!</h2>
              <p class="dash-sub">Resumo do sistema Fino Trato</p>
            </div>
            <button class="glass-btn dash-refresh-btn" @click="loadStats" :disabled="loadingStats">
              <v-icon dark size="15" :class="{ spin: loadingStats }">mdi-refresh</v-icon>
            </button>
          </div>

          <!-- Cards de métricas -->
          <div class="dash-metrics">
            <div class="metric-card glass">
              <div class="metric-icon-wrap metric-teal">
                <v-icon dark size="22">mdi-paw</v-icon>
              </div>
              <span class="metric-num">{{ stats.totalPets }}</span>
              <span class="metric-label">Pets cadastrados</span>
            </div>
            <div class="metric-card glass" :class="{ 'metric-alert': stats.missingPets > 0 }">
              <div class="metric-icon-wrap metric-red">
                <v-icon dark size="22">mdi-alert-circle-outline</v-icon>
              </div>
              <span class="metric-num">{{ stats.missingPets }}</span>
              <span class="metric-label">Desaparecidos</span>
            </div>
            <div class="metric-card glass">
              <div class="metric-icon-wrap metric-blue">
                <v-icon dark size="22">mdi-qrcode-scan</v-icon>
              </div>
              <span class="metric-num">{{ stats.scansHoje }}</span>
              <span class="metric-label">Scans hoje</span>
            </div>
            <div class="metric-card glass">
              <div class="metric-icon-wrap metric-purple">
                <v-icon dark size="22">mdi-chart-line</v-icon>
              </div>
              <span class="metric-num">{{ stats.scansSemana }}</span>
              <span class="metric-label">Scans na semana</span>
            </div>
          </div>

          <!-- Linha inferior: atividade recente + pets recentes -->
          <div class="dash-bottom">

            <!-- Atividade recente -->
            <div class="dash-panel glass">
              <h4 class="dash-panel-title">
                <v-icon dark size="15" class="mr-1">mdi-history</v-icon>
                Atividade Recente
              </h4>
              <div v-if="stats.ultimosLogs.length === 0" class="dash-panel-empty">
                <v-icon dark size="28" color="rgba(255,255,255,0.2)">mdi-map-marker-off-outline</v-icon>
                <p>Nenhuma visita registrada</p>
              </div>
              <div v-else class="dash-activity-list">
                <div
                  v-for="log in stats.ultimosLogs"
                  :key="log.Id"
                  class="activity-item"
                >
                  <div class="activity-dot missing-dot-sm"></div>
                  <div class="activity-info">
                    <span class="activity-pet">{{ log.PetNome }}</span>
                    <span class="activity-time">{{ formatDateTime(log.Timestamp) }}</span>
                  </div>
                  <a
                    v-if="log.Latitude && log.Longitude"
                    :href="`https://www.google.com/maps?q=${log.Latitude},${log.Longitude}`"
                    target="_blank"
                    class="activity-map-link"
                    title="Ver no mapa"
                  >
                    <v-icon dark size="16">mdi-map-marker-outline</v-icon>
                  </a>
                </div>
              </div>
            </div>

            <!-- Pets recentes -->
            <div class="dash-panel glass">
              <h4 class="dash-panel-title">
                <v-icon dark size="15" class="mr-1">mdi-paw</v-icon>
                Cadastros Recentes
              </h4>
              <div v-if="stats.ultimosPets.length === 0" class="dash-panel-empty">
                <v-icon dark size="28" color="rgba(255,255,255,0.2)">mdi-paw-off</v-icon>
                <p>Nenhum pet cadastrado</p>
              </div>
              <div v-else class="dash-pets-list">
                <div
                  v-for="pet in stats.ultimosPets"
                  :key="pet.Id"
                  class="dash-pet-item"
                  @click="selectedPet = pet"
                >
                  <img
                    :src="pet.B64Foto || require('@/assets/logoCard.png')"
                    class="dash-pet-thumb"
                    @error="onImgError"
                  />
                  <div class="dash-pet-info">
                    <span class="dash-pet-name">{{ pet.Nome || 'Cadastro Pendente' }}</span>
                    <span class="dash-pet-meta">{{ pet.Raca || pet.TipoPet || '—' }}</span>
                  </div>
                  <div
                    class="dash-pet-status"
                    :class="pet.isDesaparecido ? 'status-dot-missing' : 'status-dot-safe'"
                  ></div>
                </div>
              </div>
            </div>

          </div>
        </div>

        <!-- PET DETAIL -->
        <div v-else :key="selectedPet.Id" class="pet-detail">

          <!-- Pet Header Card -->
          <div class="detail-card glass pet-head-card">
            <div class="ph-photo-col">
              <div class="ph-photo-wrap" :class="{ uploading: uploadingPhoto }" @click="triggerPhotoUpload" title="Alterar foto">
                <img
                  :src="selectedPet.B64Foto || require('@/assets/logoCard.png')"
                  class="ph-photo"
                  @error="onImgError"
                />
                <div class="ph-photo-upload-overlay">
                  <v-icon dark size="22" color="white" :class="{ spin: uploadingPhoto }">{{ uploadingPhoto ? 'mdi-loading' : 'mdi-camera' }}</v-icon>
                </div>
              </div>
              <input
                ref="photoInput"
                type="file"
                accept="image/*"
                style="display:none"
                @change="uploadPhoto"
              />
              <div
                class="status-badge"
                :class="selectedPet.isDesaparecido ? 'status-missing' : 'status-safe'"
              >
                <v-icon dark size="12">
                  {{ selectedPet.isDesaparecido ? 'mdi-alert-circle' : 'mdi-check-decagram' }}
                </v-icon>
                {{ selectedPet.isDesaparecido ? 'Desaparecido' : 'Protegido' }}
              </div>
            </div>

            <div class="ph-info-col">
              <h2 class="ph-name">{{ selectedPet.Nome || 'Cadastro Pendente' }}</h2>
              <div class="ph-meta-row">
                <span class="ph-meta">
                  <v-icon dark size="14">mdi-account-outline</v-icon>
                  {{ selectedPet.Portador || 'Sem tutor' }}
                </span>
                <span class="ph-meta" v-if="selectedPet.Endereco">
                  <v-icon dark size="14">mdi-map-marker-outline</v-icon>
                  {{ selectedPet.Endereco }}
                </span>
              </div>

              <div class="ph-tags">
                <span class="glass-tag" v-if="selectedPet.TipoPet">{{ selectedPet.TipoPet }}</span>
                <span class="glass-tag" v-if="selectedPet.Raca">{{ selectedPet.Raca }}</span>
                <span class="glass-tag" v-if="selectedPet.Sexo">{{ selectedPet.Sexo }}</span>
                <span class="glass-tag" v-if="selectedPet.Idade">{{ selectedPet.Idade }} anos</span>
              </div>

              <div class="ph-actions">
                <button class="glass-btn ph-btn" @click="openEditModal">
                  <v-icon dark size="14">mdi-pencil-outline</v-icon>
                  Editar
                </button>
                <button
                  class="glass-btn ph-btn"
                  :class="selectedPet.isDesaparecido ? 'btn-success-gl' : 'btn-warn-gl'"
                  @click="toggleMissing"
                  :disabled="actionLoading"
                >
                  <v-icon dark size="14">
                    {{ selectedPet.isDesaparecido ? 'mdi-check-circle-outline' : 'mdi-alert-outline' }}
                  </v-icon>
                  {{ selectedPet.isDesaparecido ? 'Marcar Encontrado' : 'Marcar Desaparecido' }}
                </button>
                <button class="glass-btn ph-btn btn-danger-gl" @click="confirmDelete">
                  <v-icon dark size="14">mdi-delete-outline</v-icon>
                  Excluir
                </button>
              </div>
            </div>
          </div>

          <!-- Info + Obs -->
          <div class="detail-row">
            <div class="detail-card glass info-card">
              <h4 class="card-section-title">
                <v-icon dark size="16" class="mr-1">mdi-information-outline</v-icon>
                Detalhes
              </h4>
              <div class="info-grid">
                <div class="info-item">
                  <span class="info-label">Microchip</span>
                  <span class="info-val">{{ selectedPet.IdMicrochip || '—' }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Coloração</span>
                  <span class="info-val">{{ selectedPet.Coloracao || '—' }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Nascimento</span>
                  <span class="info-val">{{ formatDate(selectedPet.DataNascimento) }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Vacinas</span>
                  <span class="info-val">{{ selectedPet.Vacinas || '—' }}</span>
                </div>
              </div>
            </div>

            <div class="detail-card glass obs-card">
              <h4 class="card-section-title">
                <v-icon dark size="16" class="mr-1">mdi-note-text-outline</v-icon>
                Observações
              </h4>
              <p class="obs-text">
                {{ selectedPet.Observacoes || 'Sem observações registradas.' }}
              </p>
              <div v-if="selectedPet.Descricao" class="obs-desc">
                <span class="info-label">Descrição</span>
                <p class="obs-text mt-1">{{ selectedPet.Descricao }}</p>
              </div>
            </div>
          </div>

          <!-- QR Code Card -->
          <div class="detail-card glass qr-card">
            <h4 class="card-section-title">
              <v-icon dark size="16" class="mr-1">mdi-qrcode</v-icon>
              QR Code de Identificação
            </h4>

            <div class="qr-body">
              <div class="qr-canvas-wrap" ref="qrWrapper">
                <qrcode-vue
                  :value="qrUrl"
                  :size="160"
                  level="H"
                  render-as="canvas"
                  foreground="#0f4c4a"
                ></qrcode-vue>
                <div class="qr-logo-overlay">
                  <img src="@/assets/logo.png" />
                </div>
              </div>

              <div class="qr-info-col">
                <p class="qr-label">URL gerada:</p>
                <code class="qr-url-text">{{ qrUrl }}</code>
                <button class="glass-btn qr-dl-btn" @click="downloadQR">
                  <v-icon dark size="15">mdi-download</v-icon>
                  Baixar QR Code
                </button>
              </div>
            </div>
          </div>
        </div>
        </transition>

      </main>
    </div>

    <!-- ── MODAL CRIAÇÃO RÁPIDA ──────────────────────────── -->
    <div v-if="showQuickModal" class="modal-overlay" @click.self="showQuickModal = false">
      <div class="modal-glass glass-strong quick-modal">
        <div class="modal-header">
          <h3>Novo Cadastro Rápido</h3>
          <button class="modal-close-btn" @click="showQuickModal = false">
            <v-icon dark>mdi-close</v-icon>
          </button>
        </div>
        <div class="quick-modal-body">
          <p class="quick-modal-desc">
            Insira apenas o ID do microchip. Um link será gerado para o cliente preencher o restante do cadastro.
          </p>
          <div class="form-group">
            <label>ID do Microchip *</label>
            <input
              v-model="quickMicrochip"
              class="glass-input"
              placeholder="Ex: 900123456789"
              @keyup.enter="criarPetRapido"
              autofocus
            />
          </div>
          <div class="modal-actions">
            <button class="glass-btn" @click="showQuickModal = false">Cancelar</button>
            <button class="btn-primary" @click="criarPetRapido" :disabled="!quickMicrochip.trim() || saving">
              <v-icon dark size="15" v-if="saving" class="spin">mdi-loading</v-icon>
              <v-icon dark size="15" v-else>mdi-lightning-bolt</v-icon>
              {{ saving ? 'Criando...' : 'Gerar Link' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── MODAL LINK GERADO ──────────────────────────────── -->
    <div v-if="showLinkModal" class="modal-overlay" @click.self="showLinkModal = false">
      <div class="modal-glass glass-strong link-modal">
        <div class="link-modal-icon">
          <v-icon dark size="40" color="#2cb7b1">mdi-check-circle-outline</v-icon>
        </div>
        <h3>Cadastro criado!</h3>
        <p class="link-modal-desc">
          Copie o link abaixo e envie ao cliente para que ele complete o registro do pet.
        </p>
        <div class="link-box">
          <span class="link-text">{{ linkGerado }}</span>
          <button class="glass-btn link-copy-btn" @click="copiarLink">
            <v-icon dark size="16">{{ linkCopiado ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
            {{ linkCopiado ? 'Copiado!' : 'Copiar' }}
          </button>
        </div>
        <p class="link-hint">O cliente poderá preencher nome, foto e todas as informações do pet através desse link. Após salvar, o link expira automaticamente.</p>
        <button class="btn-primary" @click="showLinkModal = false">Entendido</button>
      </div>
    </div>

    <!-- ── ADD / EDIT MODAL ──────────────────────────────── -->
    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-glass glass-strong">
        <div class="modal-header">
          <h3>{{ editingPet ? 'Editar Pet' : 'Adicionar Novo Pet' }}</h3>
          <button class="modal-close-btn" @click="closeModal">
            <v-icon dark>mdi-close</v-icon>
          </button>
        </div>

        <div class="modal-body custom-scroll">
          <form @submit.prevent="savePet" class="pet-form">
            <div class="form-grid">
              <div class="form-group">
                <label>Nome *</label>
                <input v-model="form.Nome" required class="glass-input" placeholder="Nome do pet" />
              </div>
              <div class="form-group">
                <label>Tipo de Pet</label>
                <select v-model="form.TipoPet" class="glass-input">
                  <option value="">Selecionar...</option>
                  <option>Cachorro</option>
                  <option>Gato</option>
                  <option>Pássaro</option>
                  <option>Outro</option>
                </select>
              </div>
              <div class="form-group">
                <label>Raça</label>
                <input v-model="form.Raca" class="glass-input" placeholder="Ex: Golden Retriever" />
              </div>
              <div class="form-group">
                <label>Sexo</label>
                <select v-model="form.Sexo" class="glass-input">
                  <option value="">Selecionar...</option>
                  <option>Macho</option>
                  <option>Fêmea</option>
                </select>
              </div>
              <div class="form-group">
                <label>Idade (anos)</label>
                <input v-model.number="form.Idade" type="number" min="0" max="30" class="glass-input" placeholder="0" />
              </div>
              <div class="form-group">
                <label>Data de Nascimento</label>
                <input v-model="form.DataNascimento" type="date" class="glass-input" />
              </div>
              <div class="form-group">
                <label>Microchip ID</label>
                <input v-model="form.IdMicrochip" class="glass-input" placeholder="Ex: 900123456789" />
              </div>
              <div class="form-group">
                <label>Coloração</label>
                <input v-model="form.Coloracao" class="glass-input" placeholder="Ex: Dourado" />
              </div>
              <div class="form-group full">
                <label>Tutor / Portador</label>
                <input v-model="form.Portador" class="glass-input" placeholder="Nome do responsável" />
              </div>
              <div class="form-group full">
                <label>Endereço</label>
                <input v-model="form.Endereco" class="glass-input" placeholder="Rua, número, bairro..." />
              </div>
              <div class="form-group full">
                <label>Descrição</label>
                <textarea v-model="form.Descricao" rows="3" class="glass-input" placeholder="Breve descrição do pet..."></textarea>
              </div>
              <div class="form-group full">
                <label>Vacinas</label>
                <input v-model="form.Vacinas" class="glass-input" placeholder="Antirrábica, V10, ... (separadas por vírgula)" />
              </div>
              <div class="form-group full">
                <label>Observações</label>
                <textarea v-model="form.Observacoes" rows="2" class="glass-input" placeholder="Alergias, comportamento, etc..."></textarea>
              </div>
            </div>

            <div class="modal-actions">
              <button type="button" class="glass-btn" @click="closeModal">Cancelar</button>
              <button type="submit" class="btn-primary" :disabled="saving">
                <v-icon dark size="16" v-if="saving">mdi-loading</v-icon>
                {{ saving ? 'Salvando...' : (editingPet ? 'Salvar Alterações' : 'Adicionar Pet') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- ── DELETE CONFIRM MODAL ──────────────────── -->
    <div v-if="showDeleteConfirm" class="modal-overlay" @click.self="showDeleteConfirm = false">
      <div class="modal-glass glass-strong confirm-box">
        <v-icon dark size="48" color="#e74c3c">mdi-alert-circle-outline</v-icon>
        <h3>Excluir "{{ selectedPet ? selectedPet.Nome : '' }}"?</h3>
        <p>Esta ação não pode ser desfeita.</p>
        <div class="confirm-actions">
          <button class="glass-btn" @click="showDeleteConfirm = false">Cancelar</button>
          <button class="btn-danger-solid" @click="deletePet" :disabled="actionLoading">
            {{ actionLoading ? 'Excluindo...' : 'Excluir' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── SNACKBAR ───────────────────────────────── -->
    <div v-if="snackbar.show" class="snackbar" :class="snackbar.type">
      {{ snackbar.message }}
    </div>
  </div>
</template>

<script>
import axios from "axios";
import QrcodeVue from "qrcode.vue";
import logo from "@/assets/logo.png";

const FORM_EMPTY = () => ({
  Nome: "",
  TipoPet: "",
  Raca: "",
  Sexo: "",
  Idade: null,
  DataNascimento: "",
  IdMicrochip: "",
  Coloracao: "",
  Portador: "",
  Endereco: "",
  Descricao: "",
  Vacinas: "",
  Observacoes: "",
  isDesaparecido: false,
});

export default {
  name: "AdminView",
  components: { QrcodeVue },

  data() {
    return {
      pets: [],
      selectedPet: null,
      search: "",
      loading: false,
      actionLoading: false,
      saving: false,

      showModal: false,
      editingPet: false,
      form: FORM_EMPTY(),

      showDeleteConfirm: false,
      uploadingPhoto: false,

      showQuickModal: false,
      quickMicrochip: "",
      showLinkModal: false,
      linkGerado: "",
      linkCopiado: false,

      activeTab: "pets",
      logs: [],
      loadingLogs: false,

      stats: {
        totalPets: 0,
        missingPets: 0,
        scansHoje: 0,
        scansSemana: 0,
        ultimosPets: [],
        ultimosLogs: [],
      },
      loadingStats: false,

      adminNome: localStorage.getItem("ft_admin_nome") || "",

      snackbar: { show: false, message: "", type: "success" },
      snackbarTimer: null,
    };
  },

  computed: {
    filteredPets() {
      const q = this.search.toLowerCase();
      return this.pets.filter(
        (p) =>
          (p.Nome && p.Nome.toLowerCase().includes(q)) ||
          (p.Portador && p.Portador.toLowerCase().includes(q)) ||
          (p.IdMicrochip && p.IdMicrochip.toLowerCase().includes(q))
      );
    },

    missingCount() {
      return this.pets.filter((p) => p.isDesaparecido).length;
    },

    saudacao() {
      const h = new Date().getHours();
      if (h >= 6 && h < 12) return "Bom dia";
      if (h >= 12 && h < 18) return "Boa tarde";
      return "Boa noite";
    },

    qrUrl() {
      const base = process.env.VUE_APP_FRONTEND_URL || "https://finotrato.com.br";
      const code =
        (this.selectedPet && (this.selectedPet.IdMicrochip || this.selectedPet.Id)) ||
        "PENDENTE";
      return `${base}/consultar-pet/${code}`;
    },
  },

  mounted() {
    this.loadPets();
    this.loadStats();
  },

  methods: {
    async loadPets() {
      this.loading = true;
      try {
        const { data } = await axios.get(`${this.$api}/api/pets`);
        this.pets = data;
      } catch {
        this.showSnack("Erro ao carregar pets. Verifique a conexão com o servidor.", "error");
      } finally {
        this.loading = false;
      }
    },

    openAddModal() {
      this.quickMicrochip = "";
      this.showQuickModal = true;
    },

    async criarPetRapido() {
      if (!this.quickMicrochip.trim()) return;
      this.saving = true;
      try {
        const { data } = await axios.post(`${this.$api}/api/pets/rapido`, {
          IdMicrochip: this.quickMicrochip.trim(),
        });
        this.pets.unshift(data);
        this.showQuickModal = false;
        this.quickMicrochip = "";

        const base = window.location.origin;
        this.linkGerado = `${base}/completar-registro/${data.TokenCompletar}`;
        this.linkCopiado = false;
        this.showLinkModal = true;
      } catch (err) {
        this.showSnack(err.response?.data?.msg || "Erro ao criar registro.", "error");
      } finally {
        this.saving = false;
      }
    },

    copiarLink() {
      navigator.clipboard.writeText(this.linkGerado).then(() => {
        this.linkCopiado = true;
        setTimeout(() => { this.linkCopiado = false; }, 2500);
      });
    },

    openEditModal() {
      this.editingPet = true;
      this.form = { ...this.selectedPet };
      if (this.form.DataNascimento) {
        this.form.DataNascimento = this.form.DataNascimento.toString().split("T")[0];
      }
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
    },

    async savePet() {
      this.saving = true;
      try {
        if (this.editingPet) {
          const { data } = await axios.put(
            `${this.$api}/api/pets/${this.selectedPet.Id}`,
            this.form
          );
          const idx = this.pets.findIndex((p) => p.Id === this.selectedPet.Id);
          if (idx !== -1) this.$set(this.pets, idx, data);
          this.selectedPet = data;
          this.showSnack("Pet atualizado com sucesso!", "success");
        } else {
          const { data } = await axios.post(`${this.$api}/api/pets`, this.form);
          this.pets.unshift(data);
          this.selectedPet = data;
          this.showSnack("Pet adicionado com sucesso!", "success");
        }
        this.closeModal();
      } catch {
        this.showSnack("Erro ao salvar. Tente novamente.", "error");
      } finally {
        this.saving = false;
      }
    },

    confirmDelete() {
      this.showDeleteConfirm = true;
    },

    async deletePet() {
      this.actionLoading = true;
      try {
        await axios.delete(`${this.$api}/api/pets/${this.selectedPet.Id}`);
        this.pets = this.pets.filter((p) => p.Id !== this.selectedPet.Id);
        this.selectedPet = null;
        this.showDeleteConfirm = false;
        this.showSnack("Pet excluído.", "info");
      } catch {
        this.showSnack("Erro ao excluir.", "error");
      } finally {
        this.actionLoading = false;
      }
    },

    async toggleMissing() {
      this.actionLoading = true;
      try {
        const { data } = await axios.patch(
          `${this.$api}/api/pets/${this.selectedPet.Id}/toggle-missing`
        );
        const idx = this.pets.findIndex((p) => p.Id === this.selectedPet.Id);
        if (idx !== -1) this.$set(this.pets, idx, data);
        this.selectedPet = data;
        this.showSnack(
          data.isDesaparecido
            ? "Pet marcado como desaparecido."
            : "Pet marcado como encontrado!",
          data.isDesaparecido ? "error" : "success"
        );
      } catch {
        this.showSnack("Erro ao atualizar status.", "error");
      } finally {
        this.actionLoading = false;
      }
    },

    async downloadQR() {
      const canvas = this.$refs.qrWrapper && this.$refs.qrWrapper.querySelector("canvas");
      if (!canvas) return;

      const dl = document.createElement("canvas");
      dl.width = canvas.width;
      dl.height = canvas.height;
      const ctx = dl.getContext("2d");
      ctx.drawImage(canvas, 0, 0);

      const logoImg = new Image();
      logoImg.src = logo;
      await new Promise((r) => { logoImg.onload = r; });

      const size = canvas.width * 0.16;
      const pos = (canvas.width - size) / 2;
      ctx.fillStyle = "white";
      ctx.beginPath();
      ctx.arc(canvas.width / 2, canvas.height / 2, size / 2 + 7, 0, Math.PI * 2);
      ctx.fill();
      ctx.drawImage(logoImg, pos, pos, size, size);

      const link = document.createElement("a");
      link.download = `qr-fino-trato-${(this.selectedPet && this.selectedPet.IdMicrochip) || "pet"}.png`;
      link.href = dl.toDataURL("image/png");
      link.click();
    },

    triggerPhotoUpload() {
      this.$refs.photoInput.click();
    },

    async uploadPhoto(event) {
      const file = event.target.files[0];
      if (!file) return;

      const formData = new FormData();
      formData.append("foto", file);

      this.uploadingPhoto = true;
      try {
        const { data } = await axios.post(
          `${this.$api}/api/pets/${this.selectedPet.Id}/foto`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        const idx = this.pets.findIndex((p) => p.Id === this.selectedPet.Id);
        if (idx !== -1) this.$set(this.pets, idx, data);
        this.selectedPet = data;
        this.showSnack("Foto atualizada com sucesso!", "success");
      } catch {
        this.showSnack("Erro ao enviar foto. Tente novamente.", "error");
      } finally {
        this.uploadingPhoto = false;
        event.target.value = "";
      }
    },

    onImgError(e) {
      e.target.src = require("@/assets/logoCard.png");
    },

    formatDate(d) {
      if (!d) return "—";
      const date = new Date(d);
      if (isNaN(date)) return "—";
      return date.toLocaleDateString("pt-BR");
    },

    // ── STATS ────────────────────────────────────────────
    async loadStats() {
      this.loadingStats = true;
      try {
        const { data } = await axios.get(`${this.$api}/api/stats`);
        this.stats = data;
      } catch {
        // silencioso — dashboard é complementar
      } finally {
        this.loadingStats = false;
      }
    },

    // ── LOGS ─────────────────────────────────────────────
    async switchToLogs() {
      this.activeTab = "logs";
      if (this.logs.length === 0) await this.loadLogs();
    },

    async loadLogs() {
      this.loadingLogs = true;
      try {
        const { data } = await axios.get(`${this.$api}/api/logs`);
        this.logs = data;
      } catch {
        this.showSnack("Erro ao carregar registros de rastreamento.", "error");
      } finally {
        this.loadingLogs = false;
      }
    },

    formatDateTime(d) {
      if (!d) return "—";
      const date = new Date(d);
      if (isNaN(date)) return "—";
      return date.toLocaleString("pt-BR");
    },

    truncateUA(ua) {
      if (!ua) return "—";
      if (ua.length > 48) return ua.substring(0, 48) + "…";
      return ua;
    },

    // ── AUTH ─────────────────────────────────────────────
    logout() {
      localStorage.removeItem("ft_admin_token");
      localStorage.removeItem("ft_admin_nome");
      delete axios.defaults.headers.common["Authorization"];
      this.$router.push({ name: "login" });
    },

    // ── UTIL ─────────────────────────────────────────────
    showSnack(message, type = "success") {
      if (this.snackbarTimer) clearTimeout(this.snackbarTimer);
      this.snackbar = { show: true, message, type };
      this.snackbarTimer = setTimeout(() => {
        this.snackbar.show = false;
      }, 3500);
    },
  },
};
</script>

<style scoped>
/* ── ICON COLOR INHERITANCE ──────────────────────────── */
::v-deep .v-icon {
  color: inherit !important;
}

/* ── DASHBOARD ───────────────────────────────────────── */
.dashboard {
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: 100%;
}

.dash-greeting {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.dash-title {
  color: #fff;
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0;
}

.dash-sub {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.82rem;
  margin: 4px 0 0;
}

.dash-refresh-btn {
  padding: 8px 10px;
  border-radius: 10px;
  flex-shrink: 0;
}

/* Metric cards */
.dash-metrics {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
}

.metric-card {
  border-radius: 18px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.metric-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.3);
}

.metric-card.metric-alert {
  border-color: rgba(231, 76, 60, 0.35) !important;
}

.metric-icon-wrap {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.metric-teal  { background: rgba(44, 183, 177, 0.2); color: #5fd1cb; }
.metric-red   { background: rgba(231, 76, 60, 0.2);  color: #e74c3c; }
.metric-blue  { background: rgba(52, 152, 219, 0.2); color: #5dade2; }
.metric-purple{ background: rgba(155, 89, 182, 0.2); color: #bb8fce; }

.metric-num {
  font-size: 2rem;
  font-weight: 700;
  color: #fff;
  line-height: 1;
}

.metric-label {
  font-size: 0.74rem;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Bottom panels */
.dash-bottom {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  flex: 1;
  min-height: 0;
}

.dash-panel {
  border-radius: 18px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  overflow: hidden;
}

.dash-panel-title {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin: 0;
  display: flex;
  align-items: center;
}

.dash-panel-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: rgba(255, 255, 255, 0.3);
  font-size: 0.78rem;
  text-align: center;
}

/* Activity list */
.dash-activity-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow-y: auto;
}

.activity-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 10px;
  transition: background 0.15s;
}

.activity-item:hover {
  background: rgba(255, 255, 255, 0.06);
}

.activity-dot-sm {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #e74c3c;
  flex-shrink: 0;
}

.activity-info {
  flex: 1;
  min-width: 0;
}

.activity-pet {
  display: block;
  color: #fff;
  font-size: 0.85rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.activity-time {
  display: block;
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.72rem;
}

.activity-map-link {
  color: var(--primary-light, #5fd1cb);
  text-decoration: none;
  display: flex;
  flex-shrink: 0;
  transition: color 0.2s;
}

.activity-map-link:hover {
  color: #fff;
}

/* Pets recentes */
.dash-pets-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow-y: auto;
}

.dash-pet-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 10px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s;
}

.dash-pet-item:hover {
  background: rgba(255, 255, 255, 0.08);
}

.dash-pet-thumb {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  object-fit: cover;
  border: 1.5px solid rgba(255, 255, 255, 0.15);
  flex-shrink: 0;
}

.dash-pet-info {
  flex: 1;
  min-width: 0;
}

.dash-pet-name {
  display: block;
  color: #fff;
  font-size: 0.88rem;
  font-weight: 600;
}

.dash-pet-meta {
  display: block;
  color: rgba(255, 255, 255, 0.45);
  font-size: 0.73rem;
}

.dash-pet-status {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-dot-safe    { background: #27ae60; }
.status-dot-missing { background: #e74c3c; }

/* ── TABS ────────────────────────────────────────────── */
.header-tabs {
  display: flex;
  gap: 4px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
  padding: 4px;
}

.tab-btn {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.55);
  font-family: "Comfortaa", sans-serif;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 7px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.2s ease;
  position: relative;
}

.tab-btn.active {
  background: rgba(255, 255, 255, 0.15);
  color: #ffffff;
}

.tab-btn:hover:not(.active) {
  color: rgba(255, 255, 255, 0.8);
}

.tab-badge {
  background: rgba(231, 76, 60, 0.7);
  color: white;
  font-size: 0.65rem;
  padding: 1px 6px;
  border-radius: 50px;
  font-weight: 700;
}

/* ── ADMIN NOME ──────────────────────────────────────── */
.admin-nome-label {
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.78rem;
  display: flex;
  align-items: center;
  gap: 5px;
}

/* ── LOGOUT BTN ──────────────────────────────────────── */
.btn-logout {
  border-color: rgba(231, 76, 60, 0.3) !important;
  color: rgba(231, 76, 60, 0.8) !important;
}
.btn-logout:hover {
  border-color: rgba(231, 76, 60, 0.6) !important;
  color: #e74c3c !important;
}

/* ── LOGS SECTION ────────────────────────────────────── */
.logs-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.logs-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.logs-title {
  color: #fff;
  font-size: 1rem;
  margin: 0;
  display: flex;
  align-items: center;
}

.logs-refresh {
  font-size: 0.78rem;
  padding: 7px 14px;
  border-radius: 8px;
}

.logs-desc {
  color: rgba(255, 255, 255, 0.45);
  font-size: 0.8rem;
  line-height: 1.6;
  margin: 0;
  background: rgba(0, 0, 0, 0.15);
  border-radius: 10px;
  padding: 10px 14px;
  border-left: 3px solid rgba(44, 183, 177, 0.4);
}

.logs-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 60px 20px;
  color: rgba(255, 255, 255, 0.35);
  font-size: 0.82rem;
  text-align: center;
  line-height: 1.6;
}

.logs-table-wrap {
  border-radius: 16px;
  overflow: hidden;
}

.logs-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
}

.logs-table thead th {
  background: rgba(0, 0, 0, 0.2);
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  padding: 10px 14px;
  text-align: left;
  font-weight: 600;
  white-space: nowrap;
}

.log-row {
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  transition: background 0.15s;
}

.log-row:hover {
  background: rgba(255, 255, 255, 0.05);
}

.logs-table td {
  padding: 10px 14px;
  vertical-align: middle;
}

.log-pet-name {
  color: #fff;
  font-weight: 600;
  white-space: nowrap;
}

.log-time {
  color: rgba(255, 255, 255, 0.65);
  white-space: nowrap;
}

.log-location .maps-link {
  color: var(--primary-light, #5fd1cb);
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.78rem;
  transition: color 0.2s;
}

.log-location .maps-link:hover {
  color: #fff;
}

.log-muted {
  color: rgba(255, 255, 255, 0.3);
  font-size: 0.75rem;
}

.log-ip {
  color: rgba(255, 255, 255, 0.5);
  font-family: monospace;
  font-size: 0.78rem;
}

.log-ua {
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.75rem;
  max-width: 200px;
}

/* ── PHOTO UPLOAD OVERLAY ────────────────────────────── */
.ph-photo-wrap {
  position: relative;
  cursor: pointer;
  overflow: hidden;
}

.ph-photo-upload-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
}

.ph-photo-wrap:hover .ph-photo-upload-overlay,
.ph-photo-wrap.uploading .ph-photo-upload-overlay {
  opacity: 1;
}

/* ── TRANSITIONS ─────────────────────────────────────── */
.detail-fade-enter-active,
.detail-fade-leave-active {
  transition: opacity 0.18s ease;
}
.detail-fade-enter,
.detail-fade-leave-to {
  opacity: 0;
}

/* ── WRAPPER ─────────────────────────────────────────── */
.admin-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}

/* ── HEADER ──────────────────────────────────────────── */
.admin-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 28px;
  border-radius: 0;
  border-top: none;
  border-left: none;
  border-right: none;
  position: relative;
  z-index: 10;
  gap: 16px;
}

.header-brand {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-logo {
  width: 36px;
  height: 36px;
  object-fit: contain;
  filter: drop-shadow(0 2px 8px rgba(44, 183, 177, 0.4));
}

.header-title {
  display: block;
  color: #fff;
  font-weight: 700;
  font-size: 1rem;
  line-height: 1.1;
  letter-spacing: 1px;
}

.header-sub {
  display: block;
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.72rem;
}

.header-stats {
  display: flex;
  gap: 10px;
  flex: 1;
  justify-content: center;
}

.stat-pill {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 6px 18px;
  border-radius: 50px;
  min-width: 72px;
}

.missing-pill {
  border-color: rgba(231, 76, 60, 0.4) !important;
}

.stat-num {
  font-size: 1.2rem;
  font-weight: 700;
  color: #fff;
  line-height: 1;
}

.stat-label {
  font-size: 0.65rem;
  color: rgba(255, 255, 255, 0.55);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.header-actions {
  display: flex;
  gap: 8px;
}

/* ── BODY ────────────────────────────────────────────── */
.admin-body {
  display: grid;
  grid-template-columns: 300px 1fr;
  flex: 1;
  overflow: hidden;
  position: relative;
  z-index: 1;
  height: calc(100vh - 66px);
}

/* ── SIDEBAR ─────────────────────────────────────────── */
.admin-sidebar {
  border-radius: 0;
  border-top: none;
  border-bottom: none;
  border-left: none;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.sidebar-search {
  padding: 16px;
  position: relative;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.sb-search-icon {
  position: absolute;
  left: 26px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.4) !important;
}

.sb-input {
  padding-left: 38px !important;
}

.pet-list {
  flex: 1;
  overflow-y: auto;
}

.pet-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.2s ease, padding-left 0.2s ease, box-shadow 0.2s ease;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  animation: slideInLeft 0.3s ease both;
}

.pet-item:hover {
  background: rgba(255, 255, 255, 0.09);
  padding-left: 22px;
  box-shadow: inset 3px 0 0 rgba(44, 183, 177, 0.5);
}

.pet-item.active {
  background: rgba(44, 183, 177, 0.2);
  border-left: 3px solid var(--primary-light);
}

.pet-item.missing {
  border-left: 3px solid rgba(231, 76, 60, 0.7);
}

.pet-item-avatar {
  position: relative;
  flex-shrink: 0;
}

.pet-thumb {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.2);
}

.missing-dot {
  position: absolute;
  bottom: 1px;
  right: 1px;
  width: 11px;
  height: 11px;
  background: var(--error);
  border-radius: 50%;
  border: 2px solid rgba(15, 76, 74, 0.9);
  animation: missingPulse 1.8s ease-in-out infinite;
}

.pit-name {
  display: block;
  color: #fff;
  font-size: 0.88rem;
  font-weight: 600;
}

.pit-meta {
  display: block;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.73rem;
}

.sb-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 12px;
  color: rgba(255, 255, 255, 0.35);
  font-size: 0.82rem;
  text-align: center;
}

/* ── CONTENT ─────────────────────────────────────────── */
.admin-content {
  overflow-y: auto;
  padding: 24px;
}

.empty-state {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-glass {
  border-radius: 28px;
  padding: 56px 48px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.empty-logo {
  width: 72px;
  opacity: 0.7;
  animation: logoFloat 3.5s ease-in-out infinite;
  filter: drop-shadow(0 4px 16px rgba(44, 183, 177, 0.35));
}

.empty-glass h3 {
  color: #fff;
  font-size: 1.3rem;
  margin: 0;
}

.empty-glass p {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.85rem;
  max-width: 240px;
  margin: 0;
  line-height: 1.5;
}

/* ── DETAIL CARDS ────────────────────────────────────── */
.pet-detail {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.detail-card {
  border-radius: 20px;
  padding: 22px;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  animation: cardIn 0.45s ease both;
}

.detail-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(44, 183, 177, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.28);
}

.pet-head-card { animation-delay: 0s; }
.detail-row     { animation-delay: 0.1s; }
.qr-card        { animation-delay: 0.2s; }

.card-section-title {
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* Pet header card */
.pet-head-card {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}

.ph-photo-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.ph-photo-wrap {
  width: 120px;
  height: 120px;
  border-radius: 18px;
  overflow: hidden;
  border: 2px solid rgba(255, 255, 255, 0.2);
  position: relative;
  cursor: pointer;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.ph-photo-wrap:hover {
  border-color: rgba(44, 183, 177, 0.6);
  box-shadow: 0 0 20px rgba(44, 183, 177, 0.25);
}

.ph-photo-wrap.uploading {
  animation: uploadingPulse 1.2s ease-in-out infinite;
}

.ph-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ph-photo-upload-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.ph-photo-wrap:hover .ph-photo-upload-overlay {
  opacity: 1;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  border-radius: 50px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-safe {
  background: rgba(39, 174, 96, 0.2);
  border: 1px solid rgba(39, 174, 96, 0.5);
  color: #2ecc71;
  text-shadow: 0 0 12px rgba(39, 174, 96, 0.4);
}

.status-missing {
  background: rgba(231, 76, 60, 0.2);
  border: 1px solid rgba(231, 76, 60, 0.5);
  color: #e74c3c;
  animation: badgePulse 2s ease-in-out infinite;
  text-shadow: 0 0 12px rgba(231, 76, 60, 0.4);
}

.ph-info-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ph-name {
  color: #fff;
  font-size: 1.7rem;
  font-weight: 700;
  margin: 0;
}

.ph-meta-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ph-meta {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.82rem;
  display: flex;
  align-items: center;
  gap: 6px;
}

.ph-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.glass-tag {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.75rem;
  padding: 4px 12px;
}

.ph-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 4px;
}

.ph-btn {
  font-size: 0.78rem;
  padding: 8px 14px;
  border-radius: 10px;
}

.btn-warn-gl {
  border-color: rgba(244, 183, 64, 0.4) !important;
  color: #f4b740 !important;
}

.btn-success-gl {
  border-color: rgba(39, 174, 96, 0.4) !important;
  color: #2ecc71 !important;
}

.btn-danger-gl {
  border-color: rgba(231, 76, 60, 0.4) !important;
  color: #e74c3c !important;
}

/* Info + Obs row */
.detail-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 20px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.info-label {
  font-size: 0.68rem;
  color: rgba(255, 255, 255, 0.45);
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.info-val {
  font-size: 0.9rem;
  color: #fff;
  font-weight: 600;
}

.obs-text {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.85rem;
  line-height: 1.6;
  margin: 0;
}

.obs-desc {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

/* QR Card */
.qr-card {
  display: flex;
  flex-direction: column;
}

.qr-body {
  display: flex;
  align-items: center;
  gap: 28px;
}

.qr-canvas-wrap {
  position: relative;
  flex-shrink: 0;
  padding: 12px;
  background: white;
  border-radius: 14px;
  display: inline-block;
  transition: box-shadow 0.3s ease, transform 0.3s ease;
}

.qr-canvas-wrap:hover {
  box-shadow: 0 8px 32px rgba(44, 183, 177, 0.4);
  transform: scale(1.02);
}

.qr-logo-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 36px;
  height: 36px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  pointer-events: none;
}

.qr-logo-overlay img {
  width: 100%;
  height: auto;
}

.qr-info-col {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
}

.qr-label {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin: 0;
}

.qr-url-text {
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: var(--primary-light);
  font-size: 0.78rem;
  padding: 8px 12px;
  word-break: break-all;
  font-family: monospace;
}

.qr-input-row {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.qr-input-label {
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.45);
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.qr-input {
  max-width: 280px;
}

.qr-dl-btn {
  align-self: flex-start;
}

/* ── MODAL ───────────────────────────────────────────── */
.modal-glass {
  border-radius: 24px;
  width: 100%;
  max-width: 680px;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  animation: modalIn 0.25s ease;
}

@keyframes modalIn {
  from { opacity: 0; transform: scale(0.96) translateY(12px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.modal-header h3 {
  color: #fff;
  font-size: 1.05rem;
  margin: 0;
}

.modal-close-btn {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  padding: 4px;
  border-radius: 8px;
  transition: all 0.2s;
  display: flex;
}

.modal-close-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.modal-body {
  padding: 20px 24px;
  overflow-y: auto;
  flex: 1;
}

.pet-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group.full {
  grid-column: 1 / -1;
}

.form-group label {
  font-size: 0.74rem;
  color: rgba(255, 255, 255, 0.55);
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 8px;
}

/* ── QUICK MODAL ─────────────────────────────────────── */
.quick-modal {
  max-width: 420px;
  animation: modalIn 0.25s ease;
}

.quick-modal-body {
  padding: 20px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.quick-modal-desc {
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.84rem;
  line-height: 1.6;
  margin: 0;
}

/* ── LINK MODAL ──────────────────────────────────────── */
.link-modal {
  max-width: 480px;
  padding: 36px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  text-align: center;
  animation: modalIn 0.25s ease;
}

.link-modal-icon {
  margin-bottom: 4px;
}

.link-modal h3 {
  color: #fff;
  font-size: 1.1rem;
  margin: 0;
}

.link-modal-desc {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.84rem;
  margin: 0;
  line-height: 1.5;
}

.link-box {
  width: 100%;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  text-align: left;
}

.link-text {
  flex: 1;
  color: var(--primary-light, #5fd1cb);
  font-size: 0.78rem;
  font-family: monospace;
  word-break: break-all;
  line-height: 1.4;
}

.link-copy-btn {
  font-size: 0.78rem;
  padding: 7px 12px;
  border-radius: 8px;
  flex-shrink: 0;
  white-space: nowrap;
}

.link-hint {
  color: rgba(255, 255, 255, 0.35);
  font-size: 0.74rem;
  line-height: 1.5;
  margin: 0;
}

/* Delete confirm */
.confirm-box {
  max-width: 380px;
  padding: 36px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.confirm-box h3 {
  color: #fff;
  margin: 0;
  font-size: 1rem;
}

.confirm-box p {
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.85rem;
  margin: 0;
}

.confirm-actions {
  display: flex;
  gap: 10px;
  margin-top: 8px;
}

.btn-danger-solid {
  background: #e74c3c;
  border: none;
  border-radius: 10px;
  color: white;
  font-family: "Comfortaa", sans-serif;
  font-size: 0.88rem;
  font-weight: 700;
  padding: 10px 22px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-danger-solid:hover {
  background: #c0392b;
}

.btn-danger-solid:disabled {
  opacity: 0.6;
}

/* Loading spin */
.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* scrollbar custom class */
.custom-scroll {
  overflow-y: auto;
}

/* ── TRANSITIONS ─────────────────────────────────────── */
.detail-fade-enter-active,
.detail-fade-leave-active {
  transition: opacity 0.28s ease, transform 0.28s ease;
}
.detail-fade-enter {
  opacity: 0;
  transform: translateY(18px);
}
.detail-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* ── KEYFRAMES ───────────────────────────────────────── */
@keyframes cardIn {
  from {
    opacity: 0;
    transform: translateY(22px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-14px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes logoFloat {
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-10px); }
}

@keyframes missingPulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(231, 76, 60, 0); }
  50%       { box-shadow: 0 0 0 5px rgba(231, 76, 60, 0.28); }
}

@keyframes badgePulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(231, 76, 60, 0); }
  50%       { box-shadow: 0 0 10px 3px rgba(231, 76, 60, 0.22); }
}

@keyframes uploadingPulse {
  0%, 100% { border-color: rgba(44, 183, 177, 0.4); box-shadow: 0 0 0 0 rgba(44, 183, 177, 0); }
  50%       { border-color: rgba(44, 183, 177, 0.9); box-shadow: 0 0 18px 4px rgba(44, 183, 177, 0.3); }
}

/* ── ENHANCED TEXT ───────────────────────────────────── */
.info-label {
  font-size: 0.68rem;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.info-val {
  font-size: 0.92rem;
  color: #fff;
  font-weight: 600;
  letter-spacing: 0.2px;
}

.obs-text {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.86rem;
  line-height: 1.7;
  margin: 0;
}

.qr-url-text {
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(44, 183, 177, 0.25);
  border-radius: 8px;
  color: #5fd1cb;
  font-size: 0.78rem;
  padding: 9px 13px;
  word-break: break-all;
  font-family: monospace;
  letter-spacing: 0.3px;
}

.empty-glass h3 {
  color: #fff;
  font-size: 1.35rem;
  margin: 0;
  letter-spacing: 0.5px;
}

.empty-glass p {
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.87rem;
  max-width: 250px;
  margin: 0;
  line-height: 1.6;
}
</style>
